import { useQuery } from "@tanstack/react-query"
import { Button } from "@workspace/web-ui/components/button"
import { Skeleton } from "@workspace/web-ui/components/skeleton"
import { cn } from "@workspace/web-ui/lib/utils"
import { Loader2 } from "lucide-react"
import { toast } from "sonner"
import { useApproveRefillMutation, useRejectRefillMutation } from "../../api/refills"
import { getRefillsByIdQueryOptions } from "../../api/refills/query"
import { PatientCard } from "./patient-card"
import { RefillCard } from "./refill-card"
import { getRouteApi } from "@tanstack/react-router"
import { useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@workspace/web-ui/components/dialog"
import { formatDate } from "~/utils"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@workspace/web-ui/components/select"

interface RefillProps {
    refillId: string
    onComplete?: () => void
}

export const MedicalCalendarRefillComponent = ({ refillId, onComplete }: RefillProps) => {
    const approveRefillMutation = useApproveRefillMutation({
        refill_id: refillId
    })

    const rejectRefillMutation = useRejectRefillMutation()

    const { data, isLoading } = useQuery(getRefillsByIdQueryOptions({
        id: refillId
    }))

    const routeApi = getRouteApi("/(dashboard)/dashboard/$program");
    const { program } = routeApi.useParams();

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [reasonError, setReasonError] = useState("");
    const [rejectDetails, setRejectDetails] = useState("");
    const handleApproveRefill = async () => {
        try {
            const response = await approveRefillMutation.mutateAsync();

            if (response.status === "success") {
                toast.success('Refill Request Approved Successfully', {
                    style: {
                        background: "#10B981",
                        color: "#FFFFFF",
                        borderColor: "#10B981",
                        '& svg': {
                            color: '#FFFFFF'
                        }
                    } as React.CSSProperties
                });

                if (onComplete) onComplete();
            }
        } catch (error) {
            console.error("Error approving refill request:", error);
            return toast.error(
                'Failed to approve refill request.',
                {
                    style: {
                        background: '#DC2626',
                        color: '#FFFFFF',
                        borderColor: '#DC2626',
                        '& svg': {
                            color: '#FFFFFF'
                        }
                    } as React.CSSProperties
                }
            );
        }
    }

    const handleRejectRefill = async (reason: string, details: string) => {
        try {
            const response = await rejectRefillMutation.mutateAsync({
                refill_id: refillId,
                reason,
                details,
            });

            if (response.status === "success") {
                toast.success('Refill Request Rejected Successfully', {
                    style: {
                        background: "#10B981",
                        color: "#FFFFFF",
                        borderColor: "#10B981",
                        '& svg': {
                            color: '#FFFFFF'
                        }
                    } as React.CSSProperties
                });

                if (onComplete) onComplete();
            }
        } catch (error) {
            console.error("Error rejecting refill request:", error);
            return toast.error(
                'Failed to reject refill request.',
                {
                    style: {
                        background: '#DC2626',
                        color: '#FFFFFF',
                        borderColor: '#DC2626',
                        '& svg': {
                            color: '#FFFFFF'
                        }
                    } as React.CSSProperties
                }
            );
        }
    }

    return (
        <div
            className={cn(
                "flex flex-col gap-0 py-2 rounded-lg",
                "max-w-2xl"
            )}
            data-testid="refill-component"
        >

            <div className="space-y-2 flex flex-col overflow-y-auto hide-scrollbar">
                {isLoading ? (
                    <Skeleton className="w-full h-64" />
                ) : (
                    <>
                        {data && (
                            <>
                                <PatientCard
                                    patientName={data.patientName}
                                    patientEmail={data.patientEmail}
                                    programEnrollment={data.programEnrollment}
                                    requestDate={`${formatDate(data.requestedAt)}`}
                                    currentWeight={`${data.weightHistory.currentWeight} lbs`}
                                    patientId={data.patientId}
                                />

                                {!Array.isArray(data.medications) && (
                                    <RefillCard
                                        medicationName={data.medications.name || ""}
                                        medicationDosage={data.medications.dosage || ""}
                                        medicationQuantity={data.medications.quantity?.toString() || data.medications.refills?.toString() || ""}
                                        medicationVitamin={data.medications.vitamin || ""}
                                    />
                                )}
                            </>
                        )}

                        <div className="flex w-full gap-2 mt-6">
                            <Button className="!rounded-[8px] !bg-[#E12121] !py-[12px] w-full px-[16px]" onClick={() => setShowRejectModal(true)}>
                                {rejectRefillMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 text-white" />
                                ) : (
                                    "Reject Refill"
                                )}
                            </Button>
                            <Button className="!text-white !rounded-[8px] !border-none w-full !py-[14px]" onClick={handleApproveRefill}>
                                {approveRefillMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 text-white" />
                                ) : (
                                program === "weight-loss" ? "Send to Pharmacy" : "Approve Refill"
                                )}
                            </Button>
                        </div>
                    </>
                )}
            </div>

            <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
                <DialogContent className="rounded-[24px]">
                    <DialogHeader className="flex items-center justify-center">
                        <DialogTitle className=" text-xl font-medium">Reject Request</DialogTitle>
                    </DialogHeader>
                    <div>
                        <p className="flex items-center mb-4 text-xl font-medium justify-center text-center">
                            You are rejecting the refill request for {!Array.isArray(data?.medications) ? `${data?.medications?.name || ''} ${data?.medications?.dosage || ''}` : ''} for {data?.patientName}.
                        </p>
                        <label className="text-[14px] mt-4 text-[#1A202C]">
                        <Select value={rejectReason} onValueChange={setRejectReason}>
                              <SelectTrigger className="w-full rounded-[8px] border h-[44px] p-2 mt-2 mb-4">
                                <SelectValue placeholder="Select a reason" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  <SelectItem value="Cannot prescribe this medication">Cannot prescribe this medication</SelectItem>
                                  {program === "urgent-care" && (
                                            <SelectItem value="Need more information">Need more information</SelectItem>
                                            )}
                                  {program === "weight-loss" && (
                                    <SelectItem value="Medication Completed / Discontinued">Medication Completed / Discontinued</SelectItem>
                                  )}
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                            <label className="text-[14px] mt-4 text-[#1A202C]">Please provide additional details:</label>
                            <textarea
                                value={rejectDetails}
                                onChange={e => setRejectDetails(e.target.value)}
                                maxLength={200}
                                className="w-full rounded-[8px] border h-[164px] p-2 mt-2"
                            />
                        </label>
                        {reasonError && <div className="text-red-500">{reasonError}</div>}
                        <div className="flex justify-end gap-2 mt-4">
                            <Button onClick={() => setShowRejectModal(false)} className="bg-transparent hover:bg-transparent border border-[#DCDCDE] rounded-[8px] text-[#202020CC]">Cancel</Button>
                            <Button
                                className="!bg-[#E12121] rounded-[8px] text-white"
                                disabled={rejectRefillMutation.isPending}
                                onClick={async () => {
                                    if (!rejectReason.trim()) {
                                        setReasonError("Reason is required.");
                                        return;
                                    }
                                    if (!rejectDetails.trim()) {
                                        setReasonError("Additional details are required.");
                                        return;
                                    }
                                    setReasonError("");
                                    await handleRejectRefill(rejectReason, rejectDetails);
                                    setShowRejectModal(false);
                                }}
                            >
                                {rejectRefillMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 text-white animate-spin" />
                                ) : (
                                    "Confirm"
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    )
}